import { api } from './client';

// Shape returned by the public GET /products endpoint.
export interface Product {
  id: string;
  name: string;
  platform: string | null;
  genres: string[];
  ourPriceCents: number;
  coverUrl: string | null;
  totalQty?: number;
  available?: boolean;
  ageRating?: string | null;
  isDlc?: boolean;
}

interface ProductList {
  items: Product[];
  total: number;
  offset: number;
  limit: number;
}

export function formatEuros(cents: number): string {
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

/**
 * Normalise a Kinguin `ageRating` into a short badge label:
 *   "PEGI 18" → "18+", "USK 16" → "16+", "12" → "12+",
 *   "E10+" → "E10+", "M" / "E" / "R" → kept as-is.
 * Returns null when there's nothing meaningful to show (so the badge/block hides).
 */
export function formatAgeRating(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const stripped = raw.trim().replace(/^(pegi|usk|esrb|fsk|cero|age)\s*/i, '').trim();
  if (/^\d+$/.test(stripped)) return `${stripped}+`;
  if (stripped) return stripped.toUpperCase();
  const n = raw.match(/\d+/);
  return n ? `${n[0]}+` : null;
}

/** Homepage grid/carousel — a small set of in-stock products. */
export async function fetchProducts(limit = 8): Promise<Product[]> {
  const { data } = await api.get<ProductList>('/products', {
    // Homepage is a promoted surface — never show adult content here.
    params: { limit, sort: 'popular', inStockOnly: true, excludeAdult: true },
  });
  return data.items;
}

export interface CatalogParams {
  search?: string;
  platform?: string;
  genre?: string;
  priceMinCents?: number;
  priceMaxCents?: number;
  sort?: string;
  limit?: number;
  offset?: number;
  inStockOnly?: boolean;
}

export interface CatalogResult {
  items: Product[];
  total: number;
}

/** Catalog page — paginated, filterable product listing. */
export async function fetchCatalog(params: CatalogParams): Promise<CatalogResult> {
  const { data } = await api.get<ProductList>('/products', { params });
  return { items: data.items, total: data.total };
}

/** A system-requirements group as returned by Kinguin (e.g. Minimum/Recommended). */
export interface SystemRequirement {
  system?: string;
  requirement?: string[];
}

export interface ProductDetail extends Product {
  originalName?: string | null;
  description?: string | null;
  developers?: string[];
  publishers?: string[];
  releaseDate?: string | null;
  metacriticScore?: number | null;
  regionalLimitation?: string | null;
  regions?: string[];
  languages?: string[];
  screenshots?: string[];
  systemRequirements?: SystemRequirement[] | null;
  imageUrl?: string | null;
  qty?: number;
  isPreorder?: boolean;
}

export async function fetchProduct(id: string): Promise<ProductDetail> {
  const { data } = await api.get<ProductDetail>(`/products/${id}`);
  return data;
}

export interface Facet {
  name: string;
  // Optional: taxonomy-sourced categories may not carry a product count.
  count?: number;
}

export interface Facets {
  platforms: Facet[];
  genres: Facet[];
}

export async function fetchFacets(): Promise<Facets> {
  const { data } = await api.get<Facets>('/products/filters');
  return { platforms: data.platforms ?? [], genres: data.genres ?? [] };
}
